import type { IQueueEvent } from "../store/types";
import { dataTitles } from "./lookups/dataTitles";
import { dataUnits } from "./lookups/dataUnits";

interface IDirtyTile {
  keyName: string;
  title: string;
  units: string;
  value: number | string;
}

interface ICleanTile {
  keyName: string;
  title: string;
  units: string;
  value: number | string;
}

export const queueEventCleaner = (eventData: IQueueEvent): ICleanTile[] => {
  let allData: ICleanTile[] = [];

  if (eventData.pitch_data) {
    let pitchData = extractData(eventData.pitch_data);
    pitchData = removeUnwantedElements(
      pitchData,
      new Set([
        "eventReconstruction",
        "ExtensionAccurate",
        "ReleaseAccuracy",
        "yt_R0",
      ]),
    );
    const cleanedPitchData = cleanData(pitchData, ["Tilt", "ZoneAccuracy"]);
    allData = [...allData, ...cleanedPitchData];
  }

  if (eventData.hit_data) {
    let hitData = extractData(eventData.hit_data);
    hitData = removeUnwantedElements(
      hitData,
      new Set(["GroundAccuracy", "yt_R0", "ExitAccuracy"]),
    );
    const cleanedHitData = cleanData(hitData);
    allData = [...allData, ...cleanedHitData];
  }

  return allData;
};

const extractData = (
  rawData: Record<string, number | string>,
): IDirtyTile[] => {
  return Object.entries(rawData).map(([key, value]) => ({
    keyName: key,
    title: dataTitles[key],
    units: dataUnits[key],
    value,
  }));
};

const removeUnwantedElements = (
  data: IDirtyTile[],
  unwantedKeys: Set<string>,
): IDirtyTile[] => {
  return data.filter(({ keyName }) => !unwantedKeys.has(keyName));
};

const cleanData = (
  data: IDirtyTile[],
  isolateKeys: string[] = [],
): ICleanTile[] => {
  const isolateData: IDirtyTile[] = [];

  if (isolateKeys.length > 0) {
    data = data.filter((item) => {
      if (isolateKeys.includes(item.keyName)) {
        isolateData.push(item);
        return false;
      }
      return true;
    });
  }

  const cleanTile = ({
    keyName,
    title,
    units,
    value,
  }: IDirtyTile): ICleanTile => ({
    keyName,
    title,
    units,
    value: typeof value === "number" ? parseFloat(value.toFixed(1)) : 0,
  });

  const cleanedData = data.map(cleanTile);

  return isolateData.length > 0
    ? [...cleanedData, ...isolateData]
    : cleanedData;
};
